import React, { useState } from 'react';
import api from '../../../services/api';
import { Button } from '../../ui/button';
import { toast } from 'sonner';
import { Save, Loader2, Gem, Swords, ShieldCheck, ShoppingBag, Trophy } from 'lucide-react';
import { fmt, gbErr } from './gbAdminUtils';

const premio = (linhas, rank) => {
  const l = (linhas || []).find((p) => Number(p.rank) === rank);
  return l ? Number(l.coins) : 0;
};

const setPremio = (linhas, rank, coins) => {
  const base = [1, 2, 3].map((r) => ({ rank: r, coins: premio(linhas, r) }));
  return base.map((p) => (p.rank === rank ? { ...p, coins: Number(coins || 0) } : p));
};

const Num = ({ label, hint, value, onChange, step = 1, min = 0, testid }) => (
  <label className="block text-xs font-bold text-slate-600">
    <span className="block">{label}</span>
    <input type="number" value={value} step={step} min={min} data-testid={testid}
      onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
      className="mt-1 h-9 w-full rounded-lg border px-2 text-sm font-mono" />
    {hint && <span className="mt-1 block text-[10px] font-normal text-slate-400">{hint}</span>}
  </label>
);

const Box = ({ title, Icon, children }) => (
  <div className="rounded-xl border p-3">
    <p className="mb-3 flex items-center gap-1.5 text-xs font-black uppercase text-slate-500">
      <Icon className="h-3.5 w-3.5" /> {title}
    </p>
    {children}
  </div>
);

export default function GbEconomy({ seasonId, config, onSaved }) {
  const [eco, setEco] = useState({ ...(config.economy || {}) });
  const [lim, setLim] = useState({ ...(config.limits || {}) });
  const [items, setItems] = useState({ ...(config.shop?.items || {}) });
  const [ups, setUps] = useState(JSON.parse(JSON.stringify(config.shop?.upgrades || {})));
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const ratePct = Number(eco.diamond_rate || 0) * 100;
  const sobra = Number(eco.pvp_entry_coins || 0) * 2
    - Number(eco.pvp_winner_coins || 0) - Number(eco.pvp_house_coins || 0)
    - Number(eco.pvp_jackpot_coins || 0);

  const save = async () => {
    if (reason.trim().length < 3) { toast.error('Escreva o motivo da alteração (mín. 3 letras).'); return; }
    setBusy(true);
    try {
      await api.patch(`/admin/gb/season/${seasonId}/config`, {
        reason: reason.trim(),
        config: { economy: eco, limits: lim, shop: { items, upgrades: ups } },
      });
      toast.success('Economia atualizada e registrada na auditoria.');
      setReason('');
      await onSaved();
    } catch (e) { toast.error(gbErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="gb-admin-economy">
      <div className="grid gap-3 md:grid-cols-2">
        <Box title="Diamantes e PvP" Icon={Gem}>
          <div className="grid grid-cols-2 gap-3">
            <Num label="% das moedas viram diamantes" step={0.0001} testid="gb-eco-rate"
              value={eco.diamond_rate} onChange={(v) => setEco({ ...eco, diamond_rate: v })}
              hint={`${ratePct.toLocaleString('pt-BR', { maximumFractionDigits: 3 })}% · 100.000 moedas = ${fmt(Math.floor(100000 * (eco.diamond_rate || 0)))} 💎`} />
            <Num label="Diamantes de boas-vindas" testid="gb-eco-welcome"
              value={eco.welcome_diamonds} onChange={(v) => setEco({ ...eco, welcome_diamonds: v })}
              hint="Creditados na primeira entrada do jogador" />
            <Num label="Intercâmbio: moedas por diamante" testid="gb-eco-exchange"
              value={eco.exchange_coins_per_diamond ?? 0}
              onChange={(v) => setEco({ ...eco, exchange_coins_per_diamond: v })}
              hint="0 = intercâmbio DESLIGADO (padrão) · ex.: 2 → 1 💎 vira 2 moedas amarelas" />
            <Num label="Entrada do PvP (moedas)" testid="gb-eco-pvp-entry"
              value={eco.pvp_entry_coins} onChange={(v) => setEco({ ...eco, pvp_entry_coins: v })}
              hint={`Cada duelo arrecada ${fmt(Number(eco.pvp_entry_coins || 0) * 2)} moedas`} />
            <Num label="Prêmio do vencedor (moedas)" testid="gb-eco-pvp-prize"
              value={eco.pvp_winner_coins} onChange={(v) => setEco({ ...eco, pvp_winner_coins: v })}
              hint={sobra < 0 ? '⚠️ paga mais do que arrecada — o servidor vai limitar'
                : `Arrecadado − prêmio − banca − jackpot = ${fmt(sobra)} moedas`} />
            <Num label="Parte da banca (moedas)" testid="gb-eco-pvp-house"
              value={eco.pvp_house_coins} onChange={(v) => setEco({ ...eco, pvp_house_coins: v })}
              hint="Volta para o fundo do evento" />
            <Num label="Jackpot do PvP (moedas)" testid="gb-eco-pvp-jackpot"
              value={eco.pvp_jackpot_coins} onChange={(v) => setEco({ ...eco, pvp_jackpot_coins: v })}
              hint="Dividido no fim do evento entre o top 3 do PvP" />
            <Num label="Resgate mínimo (moedas)" testid="gb-eco-min-withdraw"
              value={eco.min_withdraw_coins}
              onChange={(v) => setEco({ ...eco, min_withdraw_coins: v, withdraw_min_admin_set: true })}
              hint="1 = sem mínimo (o cliente resgata todo o saldo)" />
          </div>
        </Box>

        <Box title="Prêmios dos rankings e pacote de moedas" Icon={Trophy}>
          <div className="grid grid-cols-3 gap-3">
            {[1, 2, 3].map((r) => (
              <Num key={`lv${r}`} label={`Nível ${r}º (moedas)`} testid={`gb-eco-level-prize-${r}`}
                value={premio(eco.level_prizes, r)}
                onChange={(v) => setEco({ ...eco, level_prizes: setPremio(eco.level_prizes, r, v) })}
                hint={r === 1 ? 'Prêmio fixo, só para o top 3' : undefined} />
            ))}
            {[1, 2, 3].map((r) => (
              <Num key={`cl${r}`} label={`Clã ${r}º (moedas)`} testid={`gb-eco-clan-prize-${r}`}
                value={premio(eco.clan_prizes, r)}
                onChange={(v) => setEco({ ...eco, clan_prizes: setPremio(eco.clan_prizes, r, v) })}
                hint={r === 1 ? 'Dividido entre os 5 que mais pontuaram no clã' : undefined} />
            ))}
            <Num label="Pacote no jogo (moedas)" testid="gb-eco-pack-coins"
              value={eco.coin_pack_coins} onChange={(v) => setEco({ ...eco, coin_pack_coins: v })}
              hint="Creditadas na carteira do evento" />
            <Num label="Preço do pacote (R$)" testid="gb-eco-pack-price" step={0.5}
              value={eco.coin_pack_price_brl}
              onChange={(v) => setEco({ ...eco, coin_pack_price_brl: v })}
              hint="Cobrado por PIX ou MB WAY, sem bônus" />
          </div>
        </Box>

        <Box title="Limites de proteção" Icon={ShieldCheck}>
          <div className="grid grid-cols-2 gap-3">
            <Num label="Duração mínima (s)" testid="gb-lim-min"
              value={lim.min_match_seconds} onChange={(v) => setLim({ ...lim, min_match_seconds: v })}
              hint="Abaixo disso não paga e marca a conta" />
            <Num label="Duração máxima (s)" testid="gb-lim-max"
              value={lim.max_match_seconds} onChange={(v) => setLim({ ...lim, max_match_seconds: v })} />
          </div>
        </Box>

        <Box title="Preço dos itens (💎 · máx. 100)" Icon={ShoppingBag}>
          <div className="grid grid-cols-3 gap-3">
            {['pocao', 'missil', 'tornado'].map((k) => (
              <Num key={k} label={k === 'pocao' ? 'Poção' : k === 'missil' ? 'Míssil' : 'Tornado'}
                testid={`gb-item-${k}`} value={items[k]} onChange={(v) => setItems({ ...items, [k]: v })} />
            ))}
          </div>
        </Box>

        <Box title="Melhorias permanentes" Icon={Swords}>
          <div className="space-y-2">
            {['dmg', 'armor', 'fuel'].map((k) => (
              <div key={k} className="grid grid-cols-4 items-end gap-2">
                <p className="pb-2 text-xs font-bold text-slate-600">
                  {k === 'dmg' ? 'Dano' : k === 'armor' ? 'Blindagem' : 'Combustível'}
                </p>
                <Num label="Preço base" testid={`gb-up-${k}-base`} value={ups[k]?.base_price}
                  onChange={(v) => setUps({ ...ups, [k]: { ...ups[k], base_price: v } })} />
                <Num label="Aumento/nível" testid={`gb-up-${k}-step`} value={ups[k]?.step}
                  onChange={(v) => setUps({ ...ups, [k]: { ...ups[k], step: v } })} />
                <Num label="Nível máx." testid={`gb-up-${k}-max`} value={ups[k]?.max_level} min={1}
                  onChange={(v) => setUps({ ...ups, [k]: { ...ups[k], max_level: v } })} />
              </div>
            ))}
          </div>
        </Box>
      </div>

      <div className="flex flex-wrap items-end gap-2 rounded-xl border border-orange-200 bg-orange-50 p-3">
        <label className="min-w-[240px] flex-1 text-xs font-bold text-slate-600">
          <span className="block">Motivo da alteração (fica na auditoria)</span>
          <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="gb-eco-reason"
            placeholder="Ex.: reduzindo a entrada das fases iniciais"
            className="mt-1 h-9 w-full rounded-lg border px-2 text-sm" />
        </label>
        <Button size="sm" disabled={busy} onClick={save} data-testid="gb-eco-save"
          className="bg-emerald-600 text-white hover:bg-emerald-700">
          {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
          Salvar economia
        </Button>
      </div>
    </div>
  );
}
